import { useCallback, useEffect, useState } from 'react';
import { queueMonitorService, QueueItem, JobItem, QueueFilters, JobFilters, QueueStats, TenantOption } from '../../../../services/queue-monitor.service';

export type { QueueItem, JobItem, QueueFilters, JobFilters, QueueStats, TenantOption };

interface UseQueueMonitorReturn {
  queues: QueueItem[];
  jobs: JobItem[];
  loading: boolean;
  error: Error | null;
  stats: QueueStats;
  tenants: TenantOption[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  jobFilters: JobFilters;
  setJobFilters: (filters: JobFilters | ((prev: JobFilters) => JobFilters)) => void;
  setJobs: (jobs: JobItem[]) => void;
  setMeta: (meta: { page: number; limit: number; total: number; totalPages: number } | ((prev: { page: number; limit: number; total: number; totalPages: number }) => { page: number; limit: number; total: number; totalPages: number })) => void;
  refetchQueues: () => Promise<void>;
  refetchJobs: (queueId: string) => Promise<void>;
  cancelJob: (id: string) => Promise<void>;
  retryJob: (id: string) => Promise<void>;
}

export const useQueueMonitor = (): UseQueueMonitorReturn => {
  const [queues, setQueues] = useState<QueueItem[]>([]);
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [stats, setStats] = useState<QueueStats>({
    total_queues: 0,
    total_jobs: 0,
    queued: 0,
    running: 0,
    completed: 0,
    failed: 0,
  });
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [jobFilters, setJobFilters] = useState<JobFilters>({ page: 1, limit: 20 });
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const response = await queueMonitorService.getStats();
      setStats(response);
    } catch (err) {
      console.error('Failed to fetch stats', err);
    }
  }, []);

  const fetchTenants = useCallback(async () => {
    try {
      const response = await queueMonitorService.getTenants();
      setTenants(response);
    } catch (err) {
      console.error('Failed to fetch tenants', err);
    }
  }, []);

  const fetchQueues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await queueMonitorService.getQueues({ page: 1, limit: 100 });
      setQueues(response.data);
      setMeta({
        page: response.meta.page,
        limit: response.meta.limit,
        total: response.meta.total,
        totalPages: response.meta.total_pages,
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch queues'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchJobsInQueue = useCallback(async (queueId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await queueMonitorService.getJobsInQueue(queueId, jobFilters);
      setJobs(response.data);
      setMeta({
        page: response.meta.page,
        limit: response.meta.limit,
        total: response.meta.total,
        totalPages: response.meta.total_pages,
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch jobs'));
    } finally {
      setLoading(false);
    }
  }, [jobFilters]);

  useEffect(() => {
    fetchStats();
    fetchTenants();
    fetchQueues();
  }, [fetchStats, fetchTenants, fetchQueues]);

  const cancelJob = async (id: string): Promise<void> => {
    await queueMonitorService.cancelJob(id);
    setJobs((prev) => prev.map((job) => (job.id === id ? { ...job, status: 'cancelled' } : job)));
    fetchStats();
  };

  const retryJob = async (id: string): Promise<void> => {
    await queueMonitorService.retryJob(id);
    setJobs((prev) => prev.map((job) => (job.id === id ? { ...job, status: 'queued', progress: 0 } : job)));
    fetchStats();
  };

  return {
    queues,
    jobs,
    loading,
    error,
    stats,
    tenants,
    meta,
    jobFilters,
    setJobFilters,
    setJobs,
    setMeta,
    refetchQueues: fetchQueues,
    refetchJobs: fetchJobsInQueue,
    cancelJob,
    retryJob,
  };
};